import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { getErrorMessage } from "@/lib/api";
import { ShieldCheck } from "lucide-react";

export default function Login() {
  const { login, loginGoogle, error, recoverAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const next = location.state?.from;
  // Empty, not a default. A real address baked in here ships to everyone who loads the page and
  // appears in every screenshot of the login screen (B-403).
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showRecover, setShowRecover] = useState(false);
  const [recoverToken, setRecoverToken] = useState("");

  const doRecover = async () => {
    try {
      const data = await recoverAdmin(recoverToken);
      toast.success(data.message || "Akun admin dipulihkan. Silakan masuk.");
      setEmail(data.email || email);
      setShowRecover(false);
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const ok = await login(email, password);
    setLoading(false);
    if (ok) navigate(typeof next === "string" && /^\/open\/[a-z_]+\/[^/]+$/.test(next) ? next : "/");
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative bg-[#0F172A]">
        <img alt="" src="https://images.unsplash.com/photo-1462556791646-c201b8241a94?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
          className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div>
            <div className="font-head text-2xl font-extrabold tracking-tight">AUDIT OS INDONESIA</div>
            <div className="text-sm text-slate-300 mt-1">Sistem Audit Berbasis Risiko · SMM · Fraud · Sampling · JET</div>
          </div>
          <div className="space-y-3 text-sm text-slate-300 max-w-md">
            <p className="font-head text-lg text-white">Berbasis risiko. Digerakkan asersi. Terlacak penuh.</p>
            <p>Setiap risiko signifikan tertaut pada respons audit. Setiap prosedur tertaut pada bukti dan kesimpulan. Gerbang kualitas memblokir penyelesaian bila syarat belum terpenuhi.</p>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center p-8 bg-background">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5" data-testid="login-form">
          <div className="flex items-center gap-2 text-primary">
            <ShieldCheck size={22} />
            <span className="font-head font-bold text-lg">Masuk ke Audit OS</span>
          </div>
          {error && <div className="text-sm text-destructive bg-red-50 border border-red-200 rounded px-3 py-2" data-testid="login-error">{error}</div>}
          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password">Kata Sandi</Label>
            <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" />
          </div>
          <Button type="submit" disabled={loading} className="w-full" data-testid="login-submit">
            {loading ? "Memproses..." : "Masuk"}
          </Button>
          <div className="relative text-center text-xs text-muted-foreground">
            <span className="bg-background px-2 relative z-10">atau</span>
            <div className="absolute inset-0 top-1/2 border-t border-border" />
          </div>
          <Button type="button" variant="outline" onClick={loginGoogle} className="w-full" data-testid="login-google">
            Masuk dengan Google
          </Button>
          <div className="pt-2 text-center">
            <button type="button" onClick={() => setShowRecover((v) => !v)}
              className="text-[11px] text-muted-foreground hover:text-foreground underline" data-testid="recover-toggle">
              Pemulihan akses admin (khusus pengembangan)
            </button>
          </div>
          {showRecover && (
            <div className="border border-border rounded-sm p-3 space-y-2 bg-muted/30" data-testid="recover-panel">
              <Label className="text-xs">Token Pemulihan</Label>
              <Input value={recoverToken} onChange={(e) => setRecoverToken(e.target.value)}
                placeholder="Masukkan ADMIN_RECOVERY_TOKEN" data-testid="recover-token" />
              <Button type="button" variant="secondary" onClick={doRecover} className="w-full h-8 text-xs" data-testid="recover-submit">
                Pulihkan Akun Super Admin
              </Button>
              <p className="text-[10px] text-muted-foreground">Memulihkan kata sandi admin yang telah ditetapkan pada server. Tidak ada kata sandi yang disimpan di frontend.</p>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
